import { useEffect, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'

import { useSnackbar } from '../../helpers/hooks/useSnackbar'
import ReservationForClientForm from '../Reservations/ReservationForClientForm/ReservationForClientForm'
import { selectUserData } from '../../reducers/userDataReducer'
import {
  selectStartDate,
  selectEndDate,
  fetchTrainingsByBuildingId,
  fetchAllTrainings,
  setTrainingsDates,
} from '../../reducers/trainingsReducer'
import { selectClients } from '../../reducers/clientsReducer'
import {
  selectAmenities,
  getAmenityReservationsInDateRange,
  getMultiDayAmenityReservationsInDateRange,
  getAmenities,
} from '../../reducers/Organisation/reservationsReducer'
import { selectActiveBuilding, setBuildings } from '../../reducers/buildingSelectorReducer'

import {
  getRangeInBuildingTz,
  getTrainingType,
  parseToBlockedTimeslots,
  parseToReservationsArray,
  mapClientToUser,
  mapTimeBlockToCalendarEvent,
} from './../../helpers/helpers'
import { getAssetLink } from './../../helpers/get-asset-link'
import EditEvent from './../CreateEvent/EditEvent'
import CreateEvent from './../CreateEvent/CreateEvent'
import Sidebar from './Sidebar/Sidebar'
import CalendarManagement from './CalendarManagement'
import CalendarUser from './CalendarUser'
import { useGetTimeBlocks } from '@/api/reservations/get-time-blocks'

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY)

const Calendar = (props) => {
  const dispatch = useDispatch()
  const { _can } = props
  const { t } = useTranslation(['calendar'])
  const { displaySnackbar } = useSnackbar()

  // TODO: Decide what to do with this. It was edited in 6th June 2025 release but in version of React it seems to not be functional.
  // componentWillMount() {
  //   const { userData, buildingData } = this.props
  //   const userRole = userData ? userData.user.role : undefined
  //   const selectedBuilding = buildingData || undefined
  //   getStartAvailablePath({ userRole, selectedBuilding }, 'communityCalendar')
  // }

  // Redux Selectors
  const userData = useSelector(selectUserData)
  const buildingData = useSelector(selectActiveBuilding)
  const users = useSelector(selectClients).map(mapClientToUser)
  const amenities = useSelector(selectAmenities)
  const startDate = useSelector(selectStartDate)
  const endDate = useSelector(selectEndDate)
  const calendar = useSelector((state) => ({
    ...state.trainings,
    ...state.reservations,
  }))
  const filters = useSelector((state) => state.filters)
  // Shift Managers have no active building in the selector - they're tied to their own building
  const calendarBuilding = buildingData || userData.buildingId
  const buildingTimezone = calendarBuilding?.timezone || 'America/Chicago'

  const { data: timeBlocks } = useGetTimeBlocks({
    dateStart: startDate,
    dateEnd: endDate,
    buildingId: calendarBuilding?.id,
  })

  const [activeWindow, setActiveWindow] = useState('')

  const fetchEvents = useCallback(() => {
    const { start, end } = getRangeInBuildingTz(startDate, endDate, buildingTimezone)

    let request
    if (userData.user?.role === 'ShiftManager') {
      request = dispatch(fetchTrainingsByBuildingId(start, end, userData.buildingId.id))
    } else if (['Administrator', 'PropertyManager'].includes(userData.user?.role) && buildingData) {
      request = dispatch(fetchTrainingsByBuildingId(start, end, buildingData.id))
    } else {
      request = dispatch(fetchAllTrainings(start, end))
    }
    request.catch(() => displaySnackbar(t('calendar:errors.loadSchedule'), true))
  }, [userData, startDate, endDate, buildingData, displaySnackbar, t])

  const fetchReservations = useCallback(() => {
    const { start: from, end: to } = getRangeInBuildingTz(startDate, endDate, buildingTimezone)
    if (userData.user?.role === 'ShiftManager') {
      dispatch(getAmenityReservationsInDateRange(userData.buildingId.id, from, to))
      dispatch(getMultiDayAmenityReservationsInDateRange(userData.buildingId.id, from, to))
    } else if (['Administrator', 'PropertyManager'].includes(userData.user?.role) && buildingData) {
      dispatch(getAmenityReservationsInDateRange(buildingData.id, from, to))
      dispatch(getMultiDayAmenityReservationsInDateRange(buildingData.id, from, to))
    }
  }, [userData, startDate, endDate, buildingData])

  const fetchAmenities = useCallback(() => {
    if (buildingData || userData.buildingId) {
      dispatch(getAmenities(buildingData?.id || userData.buildingId.id)).catch(() =>
        displaySnackbar(t('calendar:errors.loadAmenities'), true),
      )
    }
  }, [userData, buildingData, displaySnackbar, t])

  // Fetch data when component mounts
  useEffect(() => {
    if (userData.user?.role === 'PropertyManager') {
      dispatch(setBuildings(userData.buildingIds))
    }

    if (['Trainer', 'ShiftManager'].includes(userData.user?.role)) {
      fetchEvents()
      fetchReservations()
      fetchAmenities()
    }
  }, [dispatch, userData])

  const getAmenitiesByBuildingId = (buildingId) => {
    dispatch(getAmenities(buildingId)).catch(() =>
      displaySnackbar(t('calendar:errors.loadAmenities'), true),
    )
  }

  const getTrainingsByBuildingId = (start, end, buildingId) => {
    dispatch(fetchTrainingsByBuildingId(start, end, buildingId)).catch(() =>
      displaySnackbar(t('calendar:errors.loadTrainings'), true),
    )
  }

  const getReservationsByBuildingId = (start, end, buildingId) => {
    const { start: from, end: to } = getRangeInBuildingTz(start, end, buildingTimezone)
    dispatch(setTrainingsDates(from, to))
    dispatch(getAmenityReservationsInDateRange(buildingId, from, to))
    dispatch(getMultiDayAmenityReservationsInDateRange(buildingId, from, to))
  }

  const toggleWindow = (mode) => setActiveWindow(mode)

  // Map Data Function (same logic preserved)
  const mapData = (name, data) => {
    switch (name) {
      case 'Staff Member': {
        const unique = [...new Set(data.map((item) => (item.trainerId ? item.trainerId.id : null)))]
        const uniqueArray = []
        unique.forEach((id) => {
          if (id) {
            const dataObj = data.find((item) => item.trainerId && item.trainerId.id === id)
            if (dataObj && dataObj.trainerId) {
              uniqueArray.push({
                id: dataObj.trainerId.id,
                avatar: getAssetLink({
                  path: 'chitown-avatar/avatar',
                  file: dataObj.trainerId.avatar,
                }),
                text: dataObj.trainerId.firstName + ' ' + dataObj.trainerId.lastName,
              })
            }
          }
        })
        return uniqueArray
      }
      case 'Class Type': {
        const unique = [...new Set(data.map((item) => (item.type ? item.type.name : null)))]
        const uniqueArray = []
        unique.forEach((type) => {
          if (type) {
            const obj = data.filter((item) => item.type.name === type)[0]
            uniqueArray.push({
              id: obj.type.id,
              avatar: obj.type.icon,
              text: obj.type.name,
              color: obj.type.color,
              personal: obj.type.personal,
            })
          }
        })
        return uniqueArray
      }
      case 'Choose Client': {
        if (userData.user.role === 'Trainer') {
          return userData.clients.map((client) => {
            return {
              id: client.id,
              avatar: getAssetLink({ path: 'chitown-avatar/avatar', file: client.user.avatar }),
              text: client.user.firstName + ' ' + client.user.lastName,
            }
          })
        } else {
          return []
        }
      }
      case 'Choose Reservation': {
        const unique = [...new Set(data.map((item) => item.name))]
        const uniqueArray = []
        unique.forEach((type) => {
          const obj = data.filter((item) => item.name === type)[0]
          uniqueArray.push({
            id: obj.id,
            text: obj.name,
          })
        })
        return uniqueArray
      }
      case 'Select Space': {
        const selectedAmenities = filters.calendar['Choose Reservation']
        if (selectedAmenities.length === 0) {
          // Get locations from all amenities
          const allLocations = []
          data.forEach((amenity) => {
            if (amenity.locations && amenity.locations.length > 0) {
              amenity.locations.forEach((location) => {
                allLocations.push({
                  id: location.id,
                  text: location.name,
                  amenityId: amenity.id,
                })
              })
            }
          })
          return allLocations
        } else {
          // Get locations only from selected amenities
          const locations = []
          selectedAmenities.forEach((selected) => {
            const amenity = data.find((a) => a.id === selected.id)
            if (amenity && amenity.locations && amenity.locations.length > 0) {
              amenity.locations.forEach((location) => {
                locations.push({
                  id: location.id,
                  text: location.name,
                  amenityId: amenity.id,
                })
              })
            }
          })
          return locations
        }
      }
      default:
        return []
    }
  }

  // Conditional Rendering for User Roles
  if (_can('clientCalendar')) {
    return <CalendarUser />
  } else if (_can('managementCalendar')) {
    const eventTypeFilter = filters.calendar.eventType
    let filteredTrainings = calendar.trainings
    let reservations = parseToReservationsArray(
      calendar.amenityReservations,
      calendar.amenityMultiDayReservations,
      calendarBuilding,
    )

    const mappedTimeBlocks = mapTimeBlockToCalendarEvent(timeBlocks?.blocks, calendarBuilding)

    let blockedTimeslots = amenities && amenities.map((amenity) => parseToBlockedTimeslots(amenity))

    if (eventTypeFilter !== 'all') {
      switch (eventTypeFilter) {
        case 'class':
          filteredTrainings = filteredTrainings.filter((training) => {
            return getTrainingType(training) === 'class'
          })
          break
        case 'appointment':
          filteredTrainings = filteredTrainings.filter((training) => {
            return getTrainingType(training) === 'appointment'
          })
          break
        case 'event':
          filteredTrainings = filteredTrainings.filter((training) => {
            return getTrainingType(training) === 'event'
          })
          break
        case 'reservation':
          filteredTrainings = [...reservations, ...mappedTimeBlocks]
          break
        default: {
          break
        }
      }
    }

    return (
      <div className="ctp-layout__manager-calendar">
        <Sidebar
          data={amenities}
          map={mapData}
          isEvent={eventTypeFilter !== 'reservation'}
          getTrainingsByBuildingId={(start, end, building) =>
            getTrainingsByBuildingId(start, end, building)
          }
          getReservationsByBuildingId={(start, end, building) =>
            getReservationsByBuildingId(start, end, building)
          }
          getAmenitiesByBuildingId={(building) => getAmenitiesByBuildingId(building)}
          onCreateEvent={() => toggleWindow('create')}
          onCreateReservation={() => toggleWindow('reservation')}
        />
        <CalendarManagement
          data={filteredTrainings}
          blockedTimeslots={blockedTimeslots}
          getTrainingsByBuildingId={(start, end, building) =>
            getTrainingsByBuildingId(start, end, building)
          }
          getReservationsByBuildingId={(start, end, building) =>
            getReservationsByBuildingId(start, end, building)
          }
          getAmenitiesByBuildingId={(building) => getAmenitiesByBuildingId(building)}
          onReservationCancel={() => fetchReservations()}
          onEditEvent={() => toggleWindow('edit')}
          fetchEvents={(start, end) => fetchEvents(start, end)}
        />
        {activeWindow === 'create' && (
          <CreateEvent
            close={(shouldRefetch = true) => {
              toggleWindow('')
              if (shouldRefetch) {
                fetchEvents()
              }
            }}
          />
        )}
        {activeWindow === 'reservation' && (
          <Elements stripe={stripePromise}>
            <ReservationForClientForm
              handleCancel={() => {
                fetchReservations()
                toggleWindow('')
              }}
              residents={users}
              buildingData={buildingData}
            />
          </Elements>
        )}
        {activeWindow === 'edit' && (
          <EditEvent
            close={(shouldRefetch = true) => {
              toggleWindow('')
              if (shouldRefetch) {
                fetchEvents()
              }
            }}
          />
        )}
      </div>
    )
  }
}

export default Calendar
